import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  IconButton,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { createShippingCategories } from "../../../../../services/Admin/ShippingCategoriesApi";
import { getAdminCountries } from "../../../../../services/Admin/CountriesApi";

const emptyCategory = { name: "", cost: "" };

const AddShippingCategoriesDialog = ({ open, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [countriesLoading, setCountriesLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [countryId, setCountryId] = useState("");
  const [categories, setCategories] = useState([{ ...emptyCategory }]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      fetchCountries();
      resetForm();
    }
  }, [open]);

  const fetchCountries = async () => {
    setCountriesLoading(true);
    try {
      const res = await getAdminCountries();
      const data = res?.success ? res.data : res;
      setCountries(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load countries", err);
    } finally {
      setCountriesLoading(false);
    }
  };

  const resetForm = () => {
    setCountryId("");
    setCategories([{ ...emptyCategory }]);
    setError("");
  };

  const handleAddCategory = () => {
    setCategories([...categories, { ...emptyCategory }]);
  };

  const handleRemoveCategory = (index) => {
    if (categories.length > 1) {
      setCategories(categories.filter((_, i) => i !== index));
    }
  };

  const handleCategoryChange = (index, field, value) => {
    const updated = [...categories];
    updated[index][field] = value;
    setCategories(updated);
  };

  const handleSubmit = async () => {
    setError("");

    if (!countryId) {
      setError("يرجى اختيار الدولة");
      return;
    }

    const validCategories = categories.filter(
      (c) => c.name.trim() && c.cost !== "",
    );
    if (validCategories.length === 0) {
      setError("يرجى إضافة شريحة واحدة على الأقل");
      return;
    }

    const payload = {
      categories: validCategories.map((c) => ({
        name: c.name.trim(),
        cost: Number(c.cost),
        country_id: Number(countryId),
      })),
    };

    setLoading(true);
    try {
      const categoriesPayload = validCategories.map((c) => ({
        name: c.name.trim(),
        cost: Number(c.cost),
        country_id: Number(countryId),
      }));

      const res = await createShippingCategories(categoriesPayload);
      if (res?.success) {
        onSuccess?.();
        onClose();
      } else {
        setError(res?.message || "فشل إضافة الشرائح");
      }
    } catch (err) {
      setError(err?.message || "حدث خطأ أثناء الإضافة");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>إضافة شرائح شحن</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <FormControl fullWidth>
            <InputLabel>الدولة</InputLabel>
            <Select
              value={countryId}
              label="الدولة"
              onChange={(e) => setCountryId(e.target.value)}
              disabled={countriesLoading}
            >
              {countriesLoading ? (
                <MenuItem disabled>جارٍ التحميل...</MenuItem>
              ) : (
                countries.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name_ar || c.name_en}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          <Divider />

          <Box>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Typography variant="subtitle2">الشرائح</Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddCategory}
              >
                إضافة شريحة
              </Button>
            </Stack>

            <Stack spacing={2}>
              {categories.map((cat, index) => (
                <Stack
                  key={index}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                >
                  <TextField
                    label="اسم الشريحة"
                    value={cat.name}
                    onChange={(e) =>
                      handleCategoryChange(index, "name", e.target.value)
                    }
                    size="small"
                    fullWidth
                  />
                  <TextField
                    label="التكلفة"
                    type="number"
                    value={cat.cost}
                    onChange={(e) =>
                      handleCategoryChange(index, "cost", e.target.value)
                    }
                    size="small"
                    sx={{ width: 120 }}
                  />
                  <IconButton
                    color="error"
                    onClick={() => handleRemoveCategory(index)}
                    disabled={categories.length === 1}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
          </Box>

          {error && (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          إلغاء
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading}>
          {loading ? <CircularProgress size={20} /> : "حفظ"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddShippingCategoriesDialog;
